import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, LogOut, Menu } from "lucide-react";

import { logout } from "../../../services/operations/authAPI";
import { sidebarLinks } from "../../../data/dashboard-links";
import ConfirmationModal from "../../common/ConfirmationModal";
import SidebarLink from "./SidebarLink";

export default function Sidebar({ isMobileOpen, setIsMobileOpen }) {
  const { user, loading: profileLoading } = useSelector((state) => state.profile);
  const { loading: authLoading } = useSelector((state) => state.auth);
  const [isExpanded, setIsExpanded] = useState(true); // desktop shrink/expand
  const [confirmationModal, setConfirmationModal] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  if (profileLoading || authLoading) {
    return (
      <div className="flex h-[calc(100vh-4rem)] w-64 items-center justify-center bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <>
      {/* Mobile toggle button */}
      <div className="absolute left-4 top-4 z-50 flex md:hidden">
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className="rounded-lg bg-white p-2 shadow-md"
        >
          <Menu className="h-6 w-6 text-slate-700" />
        </button>
      </div>

      {/* Sidebar */}
      <div
        className={`
          fixed top-[4rem] z-40 h-[calc(100vh-4rem)] bg-white/90 backdrop-blur-sm transition-all duration-300
          bg-gradient-to-br from-slate-50 to-blue-50/30
          ${isExpanded ? "w-64" : "w-20"}
          ${isMobileOpen ? "left-0" : "-left-64"}
          md:static md:block
        `}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4">
          {isExpanded && (
            <div className="flex items-center gap-3">
              <div className="rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 p-2 shadow-lg">
                    <div className="h-6 w-6 rounded-sm bg-white"></div>
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-black">HAZOP</h2>
                    <p className="text-xs text-slate-500">Safety Software</p>
                  </div>
            </div>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="hidden md:block px-4 items-center justify-center rounded-lg p-1 hover:bg-slate-100"
          >
            {isExpanded ? (
              <ChevronLeft className=" h-5 w-5 text-slate-500" />
            ) : (
              <ChevronRight className="h-5 w-5 text-slate-500" />
            )}
          </button>
        </div>

        {/* Navigation */}
        <div className="flex flex-col py-4 px-2">
          <div className="space-y-1">
            {sidebarLinks.map((link) => {
              if (link.type && user?.accountType !== link.type) return null;
              return (
                <SidebarLink
                  key={link.id}
                  link={link}
                  iconName={link.icon}
                  isExpanded={isExpanded}
                  onClick={() => setIsMobileOpen(false)} // close on mobile
                />
              );
            })}
          </div>

          {/* Divider */}
          <div className="mx-4 my-2 h-px bg-slate-200" />

          <div className="flex flex-col space-y-1">
          <SidebarLink
            link={{
              id: "settings",
              name: "Settings",
              path: "/dashboard/settings",
              icon: "Settings",
            }}
            iconName="Settings"
            isExpanded={isExpanded}
            onClick={() => setIsMobileOpen(false)} // close on mobile
          />

          {/* Logout */}
          <button
            onClick={() =>
              setConfirmationModal({
                text1: "Are you sure?",
                text2: "You will be logged out of your account.",
                btn1Text: "Logout",
                btn2Text: "Cancel",
                btn1Handler: () => dispatch(logout(navigate)),
                btn2Handler: () => setConfirmationModal(null),
              })
            }
            className={`group flex items-center gap-3 rounded-lg px-3 py-3 text-slate-700 hover:bg-gradient-to-br hover:from-red-100 hover:to-red-200 hover:text-red-700 ${
              !isExpanded && "justify-center"
            }`}
          >
            <LogOut className="h-5 w-5" />
            {isExpanded && <span>Logout</span>}
          </button>
          </div>
        </div>
      </div>

      {confirmationModal && <ConfirmationModal modalData={confirmationModal} />}
    </>
  );
}
